import { getSyncNotificationCount } from '@/services/admin/sync-notifications';
import {
  SYNC_NOTIFICATION_CATEGORY,
  type SyncNotificationCategory,
} from '@/types/admin-sync-notification';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

/** Every inbox the 同期通知 screen shows as a tab. */
const INBOX_CATEGORIES = Object.values(SYNC_NOTIFICATION_CATEGORY);

export interface SyncNotificationUnread {
  /**
   * BD 1.0 — the header bell: every UNREAD notification in this admin's scope, across all five inboxes.
   *
   * Capped at 99+ for display. On a backlog of unmatched レセプト特定不可 rows (which nothing auto-resolves)
   * it will sit at 99+ for a while — that is the spec's choice: a bell that only watched 同期失敗 stayed
   * quiet while four other inboxes filled up.
   */
  count: number;
  /** Unread per inbox — the 同期通知 tab badges, where each number sits on the tab that opens it. */
  byCategory: Record<SyncNotificationCategory, number>;
  /**
   * BD 17.0 — records 全て再同期 would act on, per inbox. Enables the button.
   *
   * NOT the same as the unread badge: a row can be 既読 and still need a push, and a row can be unread and
   * carry no 再同期 at all (状態の不一致). Enabling the button off the badge would offer it on inboxes where
   * pressing it does nothing.
   */
  resyncPending: Record<SyncNotificationCategory, number>;
}

/**
 * Polled unread counts for the header bell badge and the 同期通知 tab badges.
 * Refetches every 60s while the tab is focused (paused in the background).
 */
export const useAdminSyncNotificationCount = () => {
  const { site } = useParams<{ site: string }>();
  const currentSite = site ?? 'medix';

  return useQuery({
    queryKey: ['sync-notifications-count', currentSite],
    queryFn: () => getSyncNotificationCount(currentSite),
    select: (res): SyncNotificationUnread => {
      const counts = res.data.by_category ?? {};
      const pending = res.data.resync_pending ?? {};
      // Read through the inbox list rather than the raw map: the server whitelist may carry a category this
      // screen has no tab for, and a badge nobody can open is worse than no badge.
      const byCategory = INBOX_CATEGORIES.reduce(
        (acc, category) => ({ ...acc, [category]: counts[category] ?? 0 }),
        {} as Record<SyncNotificationCategory, number>
      );

      const resyncPending = INBOX_CATEGORIES.reduce(
        (acc, category) => ({ ...acc, [category]: pending[category] ?? 0 }),
        {} as Record<SyncNotificationCategory, number>
      );

      // `count` is the server's total across every listed category (BD 1.0), already narrowed to the sites
      // this admin may read.
      return { count: res.data.count, byCategory, resyncPending };
    },
    refetchInterval: 60000,
    refetchIntervalInBackground: false,
  });
};
